import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ConfigProvider } from "antd";
import { ThemeContext } from "./ThemeContext";
import { getAntTheme } from "./themeConfig";
import {
  THEME_MODES,
  applyDocumentTheme,
  getStoredThemeMode,
  getSystemPrefersDark,
} from "./themeConstants";

export default function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(getStoredThemeMode);
  const [systemDark, setSystemDark] = useState(getSystemPrefersDark);

  const isDark = useMemo(() => {
    if (mode === THEME_MODES.LIGHT) return false;
    if (mode === THEME_MODES.DARK) return true;
    return systemDark;
  }, [mode, systemDark]);

  useEffect(() => {
    applyDocumentTheme(isDark);
  }, [isDark]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event) => setSystemDark(event.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const setMode = useCallback((nextMode) => {
    setModeState(nextMode);
    localStorage.setItem("themeMode", nextMode);
  }, []);

  const contextValue = useMemo(
    () => ({ mode, setMode, isDark }),
    [mode, setMode, isDark],
  );

  const antTheme = useMemo(() => getAntTheme(isDark), [isDark]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <ConfigProvider theme={antTheme}>{children}</ConfigProvider>
    </ThemeContext.Provider>
  );
}
